// T-469: base costo stagionale pura. Le osservazioni sono gia normalizzate
// dall'import; qui non si fanno conversioni, fallback o accessi esterni.

export type SeasonCostStrategy = 'weighted_average' | 'latest_valid';
export type SeasonCostSource = 'ddt' | 'invoice';

export interface SeasonCostObservation {
  foodId: string;
  dedupeKey: string;
  revision: number;
  documentDate: string;
  recordedAt: string;
  qty: number;
  unitCost: number;
  uom: string;
  currency: string;
  source: SeasonCostSource;
  validated: boolean;
  documentRef: string;
  sourceRef: string;
}

export interface SeasonCostOverride {
  revision: number;
  recordedAt: string;
  effectiveFrom: string;
  effectiveTo?: string | null;
  unitCost: number;
  uom: string;
  currency: string;
  reason: string;
  actor: string;
}

export interface SeasonCostBasisRequest {
  foodId: string;
  seasonStart: string;
  seasonEnd: string;
  asOf: string;
  observations: readonly SeasonCostObservation[];
  strategy?: SeasonCostStrategy;
  override?: SeasonCostOverride | null;
}

export type SeasonCostExclusionReason =
  | 'duplicate_revision'
  | 'different_food'
  | 'unvalidated'
  | 'invalid'
  | 'outside_season'
  | 'after_as_of'
  | 'recorded_after_as_of';

export interface SeasonCostReference {
  dedupeKey: string;
  revision: number;
  documentDate: string;
  documentRef: string;
  sourceRef: string;
  source: SeasonCostSource;
}

export interface SeasonCostProvenance {
  strategy: SeasonCostStrategy;
  basis: 'observations' | 'override';
  includedRefs: SeasonCostReference[];
  includedCount: number;
  includedQty: number | null;
  latestDocumentDate: string | null;
  exclusions: Array<{ reason: SeasonCostExclusionReason; count: number }>;
  override?: Pick<SeasonCostOverride, 'revision' | 'recordedAt' | 'effectiveFrom' | 'effectiveTo' | 'reason' | 'actor'>;
}

export interface SeasonCostBasisOk {
  status: 'ok';
  unitCost: number;
  uom: string;
  currency: string;
  provenance: SeasonCostProvenance;
}

export interface SeasonCostBasisUnavailable {
  status: 'unavailable';
  reason: 'invalid_window' | 'no_valid_observations' | 'mixed_uom' | 'mixed_currency' | 'invalid_override' | 'ambiguous_latest';
  provenance: SeasonCostProvenance;
}

export type SeasonCostBasisResult = SeasonCostBasisOk | SeasonCostBasisUnavailable;

const exclusionOrder: SeasonCostExclusionReason[] = [
  'duplicate_revision', 'different_food', 'unvalidated', 'invalid', 'outside_season', 'after_as_of', 'recorded_after_as_of',
];

/** Resolves one food's cost with an inclusive season window and inclusive as-of date. */
export function resolveSeasonCostBasis(request: SeasonCostBasisRequest): SeasonCostBasisResult {
  const strategy = request.strategy ?? 'weighted_average';
  const exclusions = new Map<SeasonCostExclusionReason, number>();
  const exclude = (reason: SeasonCostExclusionReason) => exclusions.set(reason, (exclusions.get(reason) ?? 0) + 1);
  const provenance = (basis: SeasonCostProvenance['basis'], included: SeasonCostObservation[], override?: SeasonCostOverride): SeasonCostProvenance => ({
    strategy,
    basis,
    includedRefs: included.map(toReference),
    includedCount: included.length,
    includedQty: included.length ? included.reduce((sum, item) => sum + item.qty, 0) : null,
    latestDocumentDate: included.length ? included.reduce((latest, item) => item.documentDate > latest ? item.documentDate : latest, included[0].documentDate) : null,
    exclusions: exclusionOrder.filter((reason) => exclusions.has(reason)).map((reason) => ({ reason, count: exclusions.get(reason)! })),
    ...(override ? { override: {
      revision: override.revision, recordedAt: override.recordedAt,
      effectiveFrom: override.effectiveFrom, effectiveTo: override.effectiveTo ?? null,
      reason: override.reason, actor: override.actor,
    } } : {}),
  });

  if (!isDate(request.seasonStart) || !isDate(request.seasonEnd) || !isDate(request.asOf) || request.seasonStart > request.seasonEnd) {
    return { status: 'unavailable', reason: 'invalid_window', provenance: provenance('observations', []) };
  }

  if (request.override && !isOverrideValid(request.override)) {
    return { status: 'unavailable', reason: 'invalid_override', provenance: provenance('override', [], request.override) };
  }

  if (request.override && isActiveOverride(request.override, request.asOf)) {
    const override = request.override;
    return { status: 'ok', unitCost: override.unitCost, uom: override.uom, currency: override.currency, provenance: provenance('override', [], override) };
  }

  // Prima applichiamo il cutoff di conoscenza, poi scegliamo la revisione più
  // alta nota a quella data. Così una rettifica importata domani non cancella
  // retroattivamente la versione valida di uno snapshot chiuso oggi.
  const known: SeasonCostObservation[] = [];
  for (const observation of request.observations) {
    if (!isRevisionEnvelopeValid(observation)) { exclude('invalid'); continue; }
    if (observation.recordedAt.slice(0, 10) > request.asOf) { exclude('recorded_after_as_of'); continue; }
    known.push(observation);
  }

  const winners = dedupe(known, exclude);
  const valid: SeasonCostObservation[] = [];
  for (const observation of winners) {
    // Le regole economiche si applicano solo alla revisione vincente. Una
    // rettifica gia nota che cambia alimento o data deve supersedere la vecchia
    // versione, non farla riemergere dentro la stagione.
    if (!isObservationEnvelopeValid(observation)) { exclude('invalid'); continue; }
    if (observation.foodId !== request.foodId) { exclude('different_food'); continue; }
    if (observation.documentDate < request.seasonStart || observation.documentDate > request.seasonEnd) { exclude('outside_season'); continue; }
    if (observation.documentDate > request.asOf) { exclude('after_as_of'); continue; }
    if (!observation.validated) { exclude('unvalidated'); continue; }
    if (!isObservationValueValid(observation)) { exclude('invalid'); continue; }
    valid.push(observation);
  }

  if (!valid.length) return { status: 'unavailable', reason: 'no_valid_observations', provenance: provenance('observations', []) };
  const uom = valid[0].uom;
  if (valid.some((item) => item.uom !== uom)) return { status: 'unavailable', reason: 'mixed_uom', provenance: provenance('observations', valid) };
  const currency = valid[0].currency;
  if (valid.some((item) => item.currency !== currency)) return { status: 'unavailable', reason: 'mixed_currency', provenance: provenance('observations', valid) };

  if (strategy === 'latest_valid' && latestIsAmbiguous(valid)) {
    return { status: 'unavailable', reason: 'ambiguous_latest', provenance: provenance('observations', valid) };
  }
  const included = strategy === 'latest_valid' ? [latest(valid)] : valid;
  const unitCost = strategy === 'latest_valid'
    ? included[0].unitCost
    : included.reduce((sum, item) => sum + item.qty * item.unitCost, 0) / included.reduce((sum, item) => sum + item.qty, 0);
  return { status: 'ok', unitCost, uom, currency, provenance: provenance('observations', included) };
}

function dedupe(observations: readonly SeasonCostObservation[], exclude: (reason: 'duplicate_revision') => void): SeasonCostObservation[] {
  const byKey = new Map<string, SeasonCostObservation>();
  for (const observation of observations) {
    const prior = byKey.get(observation.dedupeKey);
    if (!prior || compareRevision(observation, prior) > 0) {
      if (prior) exclude('duplicate_revision');
      byKey.set(observation.dedupeKey, observation);
    } else {
      exclude('duplicate_revision');
    }
  }
  return [...byKey.values()].sort((a, b) => a.dedupeKey.localeCompare(b.dedupeKey));
}

function compareRevision(a: SeasonCostObservation, b: SeasonCostObservation): number {
  if (a.revision !== b.revision) return a.revision - b.revision;
  // A duplicated revision is malformed; this stable tie-break keeps permutations deterministic.
  return signature(a).localeCompare(signature(b));
}

function latest(observations: readonly SeasonCostObservation[]): SeasonCostObservation {
  return [...observations].sort((a, b) => b.documentDate.localeCompare(a.documentDate)
    || b.revision - a.revision || a.dedupeKey.localeCompare(b.dedupeKey))[0];
}

function latestIsAmbiguous(observations: readonly SeasonCostObservation[]): boolean {
  const latestDate = observations.reduce((value, item) => item.documentDate > value ? item.documentDate : value, observations[0].documentDate);
  return observations.filter((item) => item.documentDate === latestDate).length > 1;
}

function toReference(item: SeasonCostObservation): SeasonCostReference {
  return { dedupeKey: item.dedupeKey, revision: item.revision, documentDate: item.documentDate, documentRef: item.documentRef, sourceRef: item.sourceRef, source: item.source };
}

function isRevisionEnvelopeValid(item: SeasonCostObservation): boolean {
  return isTimestamp(item.recordedAt) && nonBlank(item.dedupeKey)
    && Number.isInteger(item.revision) && item.revision >= 0;
}

function isObservationEnvelopeValid(item: SeasonCostObservation): boolean {
  return isDate(item.documentDate) && nonBlank(item.foodId)
    && nonBlank(item.documentRef) && nonBlank(item.sourceRef);
}

function isObservationValueValid(item: SeasonCostObservation): boolean {
  return isPositive(item.qty) && isPositive(item.unitCost)
    && nonBlank(item.uom) && nonBlank(item.currency);
}

function isActiveOverride(override: SeasonCostOverride, asOf: string): boolean {
  return override.recordedAt.slice(0, 10) <= asOf
    && override.effectiveFrom <= asOf && (!override.effectiveTo || asOf <= override.effectiveTo);
}

function isOverrideValid(override: SeasonCostOverride): boolean {
  return isDate(override.effectiveFrom)
    && (!override.effectiveTo || (isDate(override.effectiveTo) && override.effectiveTo >= override.effectiveFrom))
    && Number.isInteger(override.revision) && override.revision >= 0 && isTimestamp(override.recordedAt)
    && isPositive(override.unitCost) && nonBlank(override.uom) && nonBlank(override.currency)
    && nonBlank(override.reason) && nonBlank(override.actor);
}

function isDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(date.valueOf()) && date.toISOString().slice(0, 10) === value;
}

function isPositive(value: number): boolean { return Number.isFinite(value) && value > 0; }
function nonBlank(value: string): boolean { return value.trim().length > 0; }
function isTimestamp(value: string): boolean { return nonBlank(value) && Number.isFinite(Date.parse(value)); }
function signature(item: SeasonCostObservation): string { return JSON.stringify(item); }
